import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X, Check, AlertTriangle, Info } from "lucide-react";
import { subscribeToasts, toast } from "@/lib/toast";

const BADGE = {
  success: { I: Check, cls: "bg-emerald-500", label: "Done" },
  error: { I: AlertTriangle, cls: "bg-rose-500", label: "Couldn't complete" },
  info: { I: Info, cls: "bg-[#706D10]", label: "Notice" },
};

function Card({ t }) {
  const [hover, setHover] = useState(false);
  const left = useRef(t.duration);
  const started = useRef(Date.now());
  const reduce = useReducedMotion();

  // auto-dismiss, paused while hovered; restarts when the same message is shown again
  useEffect(() => { left.current = t.duration; }, [t.bump, t.duration]);
  useEffect(() => {
    if (hover) return;
    started.current = Date.now();
    const id = setTimeout(() => toast.dismiss(t.id), left.current);
    return () => { clearTimeout(id); left.current = Math.max(800, left.current - (Date.now() - started.current)); };
  }, [hover, t.id, t.bump]);

  const b = BADGE[t.type] || BADGE.info;
  return (
    <motion.li
      layout={!reduce}
      initial={reduce ? { opacity: 0 } : { x: "calc(100% + 32px)", opacity: 0.6 }}
      animate={{ x: 0, opacity: 1 }}
      exit={reduce ? { opacity: 0 } : { x: "calc(100% + 32px)", opacity: 0, transition: { duration: 0.28, ease: [0.4, 0, 1, 1] } }}
      transition={{ type: "spring", stiffness: 420, damping: 34, mass: 0.9 }}
      drag={reduce ? false : "x"} dragConstraints={{ left: 0, right: 0 }} dragElastic={{ left: 0.05, right: 0.9 }}
      onDragEnd={(_, i) => { if (i.offset.x > 70 || i.velocity.x > 500) toast.dismiss(t.id); }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      className="mac-toast pointer-events-auto relative w-full select-none cursor-default"
      role={t.type === "error" ? "alert" : "status"} data-type={t.type} data-sonner-toast=""
    >
      <button onClick={() => toast.dismiss(t.id)} aria-label="Dismiss notification"
        className={`absolute -left-2 -top-2 w-6 h-6 rounded-full bg-white/95 border border-black/10 shadow flex items-center justify-center text-neutral-600 transition-opacity ${hover ? "opacity-100" : "opacity-0 focus:opacity-100"}`}>
        <X className="w-3.5 h-3.5"/>
      </button>
      <div className="flex items-start gap-3 px-3.5 py-3">
        <span className="relative shrink-0">
          <img src="/icons/icon-192.png" alt="" className="w-[38px] h-[38px] rounded-[9px] shadow-sm"/>
          <span className={`absolute -right-1 -bottom-1 w-[17px] h-[17px] rounded-full ring-2 ring-white flex items-center justify-center text-white ${b.cls}`} aria-hidden><b.I className="w-[10px] h-[10px]" strokeWidth={3}/></span>
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[13px] font-semibold text-neutral-900 truncate">{t.title || "DartMyVenue"}</span>
            <span className="text-[12px] text-neutral-500 shrink-0">now</span>
          </div>
          <p className="text-[13.5px] leading-snug text-neutral-800 mt-0.5 break-words"><span className="sr-only">{b.label}: </span>{t.text}</p>
          {t.description && <p className="text-[12.5px] leading-snug text-neutral-500 mt-0.5">{t.description}</p>}
        </div>
      </div>
    </motion.li>
  );
}

/** macOS-style notifications: compact cards that slide in from the right edge, top-right of the screen. */
export default function MacToaster() {
  const [list, setList] = useState([]);
  useEffect(() => subscribeToasts((l) => setList([...l])), []);
  return (
    <ol className="fixed z-[200] right-3 sm:right-4 top-[76px] w-[min(356px,calc(100vw-24px))] flex flex-col gap-2 pointer-events-none" aria-live="polite" data-testid="toaster">
      <AnimatePresence initial={false}>
        {list.map((t) => <Card key={t.id} t={t}/>)}
      </AnimatePresence>
    </ol>
  );
}
